import moment from 'moment';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { Alert, Button, Dropdown, Form, Modal, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';

import DeployMode from '../../../models/deploy/DeployMode';
import {
  getModelFilePath,
  getModelFolders,
  ModelFolder,
  transformModelName,
} from '../../../models/ModelItem';
import Store from '../../../store';
import {
  IS_LLTB,
  MODEL_TYPES_NAMES,
  TENANT_CONFIG_REPO,
  TENANT_CONFIG_DEFAULT_BRANCH,
} from '../../../utils/constants';
import { buildAdminUrl } from '../../utils/adminUrl';
import launchAirflowDag from '../../../utils/launch_airflow_dag';
import './deployModal.css';

/** How often the deploy status is re-read while one is running. */
const POLL_MS = 2000;

type Choice = {
  mode: DeployMode;
  label: string;
  /** What deploying this way does, in the words the old cards used. */
  detail: React.ReactNode;
  /** Why it cannot be picked, if it cannot. */
  unavailable?: string;
};

interface DeployModalProps {
  store?: Store;
  show: boolean;
  onHide: () => void;
}

/**
 * Deploying, as a modal opened from the model header.
 *
 * It replaces a whole tab. That tab held one card per deploy mode, each a page
 * of prose ending in the same three controls, and reaching it meant leaving
 * whatever page you were on: deploying is the last thing you do to a model, so
 * the tab was a destination you passed through once and then had no reason to
 * visit again.
 *
 * Everything that tab could do is here, except choosing Batch or Realtime: a
 * Fit model deploys to both (see `folders`). The two Fit modes are a choice
 * rather than two cards, since only one of them can be taken. Download code and
 * Go to Processes are behind Advanced, unchanged and still SuperKudu only.
 *
 * The old two-step, a card button that opened a confirmation, is one step: this
 * modal IS the confirmation, so the warnings that used to appear in the second
 * dialog are on it before Deploy is pressed rather than after.
 */
export default inject('store')(
  observer(
    class DeployModal extends React.Component<
      DeployModalProps,
      {
        mode: DeployMode;
        advancedOpen: boolean;
        loadingCommits: boolean;
        impactLaunching: boolean;
        impactStarted: boolean;
        impactError: string | null;
      }
    > {
      interval: number;

      constructor(props: DeployModalProps) {
        super(props);
        this.state = {
          mode: 'standard',
          advancedOpen: false,
          loadingCommits: false,
          impactLaunching: false,
          impactStarted: false,
          impactError: null,
        };
        this.deploy = this.deploy.bind(this);
        this.startImpactAnalysis = this.startImpactAnalysis.bind(this);
      }

      componentDidUpdate(previous: DeployModalProps) {
        if (this.props.show && !previous.show) {
          // Reopened, so the previous run's outcome is not this run's state.
          this.props.store.deployPage.deploymentError = null;
          this.setState({ impactStarted: false, impactError: null });
          void this.loadCommits(this.state.mode);
        }
      }

      componentWillUnmount() {
        clearInterval(this.interval);
      }

      /**
       * The folders this deploy writes. A Fit model always deploys to both
       * Batch and Realtime: that is what every new model defaults to, and the
       * checkboxes that could turn one off were almost never used. `deploy`
       * saves both as on before writing, because undeploy and the computation
       * sync read these flags to decide which folders the model lives in.
       */
      folders(): ModelFolder[] {
        const { activeModel } = this.props.store;
        return getModelFolders(
          { type: activeModel.type, batch: true, realtime: true },
          { forDeployment: true }
        );
      }

      /**
       * Who last wrote each folder, so a change made outside Data Studio can be
       * warned about BEFORE Deploy is pressed. The old flow fetched these on the
       * way into the confirmation dialog, which is why that dialog had a spinner
       * on the button that opened it.
       */
      async loadCommits(mode: DeployMode) {
        const folders = this.folders();
        if (!folders.length) return;
        this.setState({ loadingCommits: true });
        try {
          await Promise.all(
            folders.map((folder) =>
              this.props.store.getLastCommit(folder, mode)
            )
          );
        } finally {
          this.setState({ loadingCommits: false });
        }
      }

      choices(): Choice[] {
        const { activeModel, tenantModels } = this.props.store;
        const typeName = MODEL_TYPES_NAMES[activeModel.type];

        if (activeModel.type === 'customer_fit') {
          const hasOtherPrimary = tenantModels.some(
            (model) =>
              model.live &&
              model.is_primary &&
              model.modelId !== activeModel.modelId
          );
          return [
            {
              mode: 'standard',
              label: 'Primary Fit model',
              detail: 'Replaces the current Primary Fit model, if there is one.',
            },
            {
              mode: 'multi',
              label: 'Secondary Fit model',
              detail: 'Runs alongside the Primary, replacing nothing.',
              unavailable: hasOtherPrimary
                ? undefined
                : 'Deploy a Primary Fit model first.',
            },
          ];
        }

        if (activeModel.type === 'mqa') {
          const multi =
            activeModel?.multiAccountEngagement ||
            tenantModels.some(
              (model) => model.type === 'mqa' && model.multiAccountEngagement
            );
          const hasOtherPrimary = tenantModels.some(
            (model) =>
              model.live &&
              model.is_primary &&
              model.type === 'mqa' &&
              model.modelId !== activeModel.modelId
          );
          const standard: Choice = {
            mode: 'standard',
            label: `${typeName} model`,
            detail: `Replaces the current ${typeName} model, if there is one.`,
          };
          if (!multi) return [standard];
          return [
            standard,
            {
              mode: 'multi',
              label: `Secondary ${typeName} model`,
              detail: `Runs alongside the standard model, replacing nothing.`,
              unavailable: hasOtherPrimary
                ? undefined
                : `Deploy a standard ${typeName} model first.`,
            },
          ];
        }

        return [
          {
            mode: 'standard',
            label: IS_LLTB(activeModel.type)
              ? `${typeName} and Lead Grade models`
              : `${typeName} model`,
            detail: `Replaces the current ${typeName} model, if there is one.`,
          },
        ];
      }

      /**
       * Deploy, then watch it finish.
       *
       * The dialog closes on success and stays open on failure, so the error
       * has something to render on. The Deployment page did the same thing
       * from the other end: `updateDeployStatus` closed its confirmation
       * dialog itself, which meant the page owned a modal it did not open.
       */
      async deploy() {
        const { store } = this.props;
        const { activeModel } = store;
        if (
          activeModel.type === 'customer_fit' &&
          !(activeModel.batch && activeModel.realtime)
        ) {
          // Before anything is written, and stop if it fails: deploying the
          // folders without saving the flags leaves undeploy and the
          // computation sync reading a context the model was not deployed to.
          try {
            await store.updateModelContext({ batch: true, realtime: true });
          } catch {
            store.deployPage.deploymentError =
              'Could not switch on Batch and Realtime for this model, so nothing was deployed. Please try again.';
            return;
          }
        }
        await store.deployModel(this.folders(), this.state.mode);
        if (store.deployPage.isDeploying) {
          this.interval = window.setInterval(async () => {
            await store.updateDeployStatus();
            if (store.deployPage.isDeploying) return;
            clearInterval(this.interval);
            this.finish();
          }, POLL_MS);
          return;
        }
        this.finish();
      }

      finish() {
        if (this.props.store.deployPage.deploymentError) return;
        this.props.onHide();
      }

      async startImpactAnalysis() {
        if (this.state.impactLaunching) return;
        const { activeModel, tenant, email } = this.props.store;
        this.setState({ impactLaunching: true, impactError: null });
        try {
          await launchAirflowDag({
            tenant,
            dagName: 'deployment_impact_analysis',
            dagConf: {
              model_id: activeModel.modelId,
              model_type: activeModel.type,
              TENANT: tenant,
              email,
            },
          });
          this.setState({ impactLaunching: false, impactStarted: true });
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const err = error as any;
          this.setState({
            impactLaunching: false,
            impactError:
              err.response?.data?.message ||
              err.message ||
              'Failed to start the analysis. Please try again.',
          });
        }
      }

      downloadConfig(folder: ModelFolder, observations?: true) {
        const { tenant, modelId } = this.props.store;
        const encoded = encodeURIComponent(folder);
        const url = observations
          ? `/api/tenant/${tenant}/model/${modelId}/config/${encoded}/observations/observations_${tenant}.yaml`
          : `/api/tenant/${tenant}/model/${modelId}/config/${encoded}/${
              this.state.mode
            }/${encoded}_${
              modelId ? `${tenant}_${modelId}.yaml` : `${tenant}.yaml`
            }`;
        window.location.href = url;
      }

      render() {
        const { show, onHide, store } = this.props;
        const { activeModel, deployPage, isSuperKudu, tenant, email } = store;
        const { deploymentError, isDeploying, lastCommits } = deployPage;
        const { mode, loadingCommits } = this.state;

        const choices = this.choices();
        const folders = this.folders();
        const isFit = activeModel.type === 'customer_fit';
        const chosen = choices.find((choice) => choice.mode === mode);

        // Only the folders this deploy would write. `lastCommits` accumulates
        // every folder ever asked about, including ones Batch/Realtime has
        // since switched off.
        const unconventional = Object.entries(lastCommits)
          .filter(([folder]) => folders.includes(folder as ModelFolder))
          .filter(([, commit]) => commit && !commit.isDeployValid);

        const blocked =
          activeModel.live ||
          !folders.length ||
          !!chosen?.unavailable ||
          (!!unconventional.length && !isSuperKudu);

        return (
          <Modal show={show} onHide={onHide} size="lg" className="dpl">
            <Modal.Header closeButton={!isDeploying}>
              <Modal.Title>Deploy {activeModel.name}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {activeModel.live && (
                <Alert variant="info">
                  <strong>This model is already deployed.</strong> Undeploy it
                  or duplicate it to deploy again.
                </Alert>
              )}
              {!activeModel.live && activeModel.lastDeployedAt && (
                <p className="text-muted small">
                  Last deployed{' '}
                  {moment(activeModel.lastDeployedAt).format(
                    'MMM DD, YYYY h:mma'
                  )}
                  {activeModel.lastDeployedBy &&
                    ` by ${activeModel.lastDeployedBy}`}
                  .
                </p>
              )}
              {deploymentError && (
                <Alert variant="danger">{deploymentError}</Alert>
              )}

              {/* A choice, not two cards. Only one of them can be taken, and
                  the pair of full-width cards the tab used gave no sign of
                  that. A single mode renders as its description alone: a radio
                  group of one is a control with nothing to decide. */}
              {choices.length > 1 ? (
                <fieldset className="mb-3">
                  {choices.map((choice) => (
                    <Form.Check
                      key={choice.mode}
                      type="radio"
                      name="deploy-mode"
                      id={`deploy-mode-${choice.mode}`}
                      checked={mode === choice.mode}
                      disabled={isDeploying || !!choice.unavailable}
                      onChange={() => {
                        this.setState({ mode: choice.mode });
                        void this.loadCommits(choice.mode);
                      }}
                      className="mb-2"
                      label={
                        <>
                          <span className="fw-semibold">{choice.label}</span>
                          <span className="d-block text-muted small">
                            {choice.unavailable ?? choice.detail}
                          </span>
                        </>
                      }
                    />
                  ))}
                </fieldset>
              ) : (
                <p className="text-muted small">{choices[0].detail}</p>
              )}

              <p className="text-muted small">
                Scores update in the integrations where a push is configured,
                with the next sync.
              </p>

              {/* ONE warning, not one per folder.
                  
                  A fit model writes three or four config folders and the bot
                  that maintains them touches all of them in the same second,
                  so this fired three times over, in three near-identical
                  paragraphs, and the dialog became a wall of yellow before
                  anything you could act on. The fact is singular: this model
                  was last changed outside Data Studio. Which folders, by whom
                  and when is the detail, and it is SuperKudu's detail. */}
              {!!unconventional.length && (
                <Alert variant="warning">
                  <strong>
                    This model was last changed outside Data Studio.
                  </strong>{' '}
                  {isSuperKudu ? (
                    <>
                      Deploying from here will overwrite those edits.
                      <ul className="mb-0 mt-2 small">
                        {unconventional.map(([folder, commit]) => (
                          <li key={`warn_${folder}`}>
                            <code>{folder}</code>, by {commit.author} on{' '}
                            {moment(commit.date).format('MMM DD, YYYY')}.{' '}
                            <a
                              target="_blank"
                              rel="noopener noreferrer"
                              href={`https://github.com/HGData/${TENANT_CONFIG_REPO}/tree/${TENANT_CONFIG_DEFAULT_BRANCH}/${getModelFilePath(
                                {
                                  tenant,
                                  modelType: activeModel.type,
                                  modelId: activeModel.modelId,
                                  folder: folder as ModelFolder,
                                  mode,
                                }
                              )}`}
                            >
                              View file
                            </a>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <>
                      Please do not deploy; contact
                      customersupport@hginsights.com to proceed.
                    </>
                  )}
                </Alert>
              )}

              {/* Information, not a warning. A yellow alert is the loudest
                  thing this app has and this is a naming convention: what a
                  Secondary is CALLED downstream, which is worth knowing
                  before you deploy and is not a problem. The names are the
                  reading, so they are set as code rather than bolded into a
                  paragraph. */}
              {mode !== 'standard' && isSuperKudu && isFit && (
                <Alert variant="light">
                  Deployed as <code>{transformModelName(activeModel.name)}</code>
                  , so Redshift tables are suffixed{' '}
                  <code>_{transformModelName(activeModel.name)}</code> and the
                  realtime API returns{' '}
                  <code>
                    customer_fit_{transformModelName(activeModel.name)}
                  </code>
                  . Rename it on the{' '}
                  <Link to={`/tenant/${tenant}`}>tenant page</Link> to change
                  this.
                </Alert>
              )}

              <button
                type="button"
                className="dpl-advanced-toggle"
                aria-expanded={this.state.advancedOpen}
                onClick={() =>
                  this.setState({ advancedOpen: !this.state.advancedOpen })
                }
              >
                <span aria-hidden>
                  {this.state.advancedOpen ? '▼' : '►'}
                </span>{' '}
                Advanced
              </button>

              {this.state.advancedOpen && (
                <div className="dpl-advanced">
                  <div className="dpl-adv-block">
                    <h6 className="dpl-adv-title">Deployment impact</h6>
                    <p className="dpl-adv-note">
                      A comparative report, emailed to {email} in about 3
                      hours. Does not touch the live model.
                    </p>
                    {this.state.impactError && (
                      <Alert variant="danger">{this.state.impactError}</Alert>
                    )}
                    {this.state.impactStarted ? (
                      <span className="dpl-adv-done">
                        Analysis started.
                      </span>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={this.startImpactAnalysis}
                        disabled={this.state.impactLaunching}
                      >
                        {this.state.impactLaunching
                          ? 'Starting analysis…'
                          : 'Start analysis'}
                      </Button>
                    )}
                  </div>

                  {isSuperKudu && (
                    <div className="dpl-adv-block">
                      <h6 className="dpl-adv-title">Configuration</h6>
                      <Dropdown className="d-inline-block me-2">
                        <Dropdown.Toggle size="sm" variant="outline-secondary">
                          Download code
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          {folders.map((folder) => (
                            <Dropdown.Item
                              key={`dl_${folder}`}
                              onClick={() => this.downloadConfig(folder)}
                            >
                              Download {folder} config
                            </Dropdown.Item>
                          ))}
                          {isFit && (
                            <>
                              <Dropdown.Divider />
                              {folders.map((folder) => (
                                <Dropdown.Item
                                  key={`dlo_${folder}`}
                                  onClick={() =>
                                    this.downloadConfig(folder, true)
                                  }
                                >
                                  Download observations {folder} config
                                </Dropdown.Item>
                              ))}
                            </>
                          )}
                        </Dropdown.Menu>
                      </Dropdown>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        href={buildAdminUrl(tenant, '/settings/processes')}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Go to Processes
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {isDeploying && (
                <p className="text-muted small mb-0 mt-3">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Pushing the configuration and waiting for it to land. The
                  dialog closes itself when it is done.
                </p>
              )}
            </Modal.Body>
            {/* `light` rather than `secondary` for Cancel: a solid grey
                button beside a solid blue one gives equal weight to the thing
                you came here to do and the thing Escape already does. */}
            <Modal.Footer>
              <Button variant="light" onClick={onHide} disabled={isDeploying}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={this.deploy}
                disabled={blocked || isDeploying || loadingCommits}
              >
                {isDeploying
                  ? 'Deploying...'
                  : `Deploy${
                      choices.length > 1 && isFit
                        ? ` as ${mode === 'multi' ? 'Secondary' : 'Primary'} Fit`
                        : ''
                    }`}
              </Button>
            </Modal.Footer>
          </Modal>
        );
      }
    }
  )
);
